/**
 * «Is this browser offline right now?» — one source for every screen that has
 * to say so instead of looking broken.
 *
 * Measured 22.10 on a Redmi with the network cut (CDP offline): a tap on a lot
 * in the catalogue opened `/tender/<slug>` as header, «← Вернуться к каталогу»,
 * footer and NOTHING — TanStack Query PAUSES a query while the browser is
 * offline (`fetchStatus: 'paused'`, neither loading nor error), and no state of
 * the page described a paused one. The search said «Ищем дольше обычного. Сайт
 * работает…», which without a network is not true either.
 *
 * `navigator.onLine === false` is the reliable direction: browsers report it
 * when there is no network interface/connection at all. `true` proves less (a
 * captive portal is "online"), so nothing here claims the network WORKS — only
 * that it is definitely gone.
 *
 * SSR/prerender-safe: no `navigator` on the server means "online", and the hook
 * hydrates from that same server answer before reading the real one, so a
 * prerendered page never mismatches.
 */
import { useSyncExternalStore } from 'react';

/** True only when the browser positively reports no connection. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

const getSnapshot = (): boolean => !isOffline();
/** The server (and hydration) always renders the online page. */
const getServerSnapshot = (): boolean => true;

/** Live online state: re-renders on the `online` / `offline` events. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
